/**
 * GroupRequestDetailsDialog Component
 * Read-only dialog showing complete details of a group request
 *
 * Features:
 * - Displays all request information in organized sections
 * - Shows group details, justification, approvers, and metadata
 * - Read-only view (no actions)
 * - Responsive design for mobile and desktop
 * - Reusable across user "My Requests" and admin "Pending Requests" views
 */

import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as ApprovedIcon,
  PendingActions as PendingIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { GroupRequest, RequestStatus } from '../../api/data-contracts';
import { useUsers } from '../../hooks/api/useUsers';
import { formatDistanceToNow } from '../../utils/formatting/dateUtils';

interface GroupRequestDetailsDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * The group request to display
   */
  request: GroupRequest | null;
  /**
   * Callback when dialog is closed
   */
  onClose: () => void;
}

export const GroupRequestDetailsDialog: React.FC<GroupRequestDetailsDialogProps> = ({
  open,
  request,
  onClose,
}) => {
  const { t } = useTranslation();

  // Fetch users for displaying names
  const { data: usersData } = useUsers({ skip: 0, limit: 1000 });
  const allUsers = usersData?.users || [];

  // Create user lookup map
  const userMap = useMemo(() => {
    const map = new Map();
    allUsers.forEach((user) => {
      map.set(user.id, user);
    });
    return map;
  }, [allUsers]);

  // Helper to get user display name
  const getUserDisplayName = (userId: string): string => {
    const user = userMap.get(userId);
    if (!user) {return userId;}
    return user.full_name || user.username || user.email || userId;
  };

  // Helper to get user email
  const getUserEmail = (userId: string): string | null => {
    const user = userMap.get(userId);
    return user?.email || null;
  };

  // Helper to get status color
  const getStatusColor = (
    status: RequestStatus
  ): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' => {
    switch (status) {
      case RequestStatus.Pending:
        return 'warning';
      case RequestStatus.Approved:
        return 'success';
      case RequestStatus.Rejected:
        return 'error';
      case RequestStatus.Cancelled:
        return 'info';
      case RequestStatus.Failed:
        return 'error';
      default:
        return 'default';
    }
  };

  // Helper to get status icon
  const getStatusIcon = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.Pending:
        return <PendingIcon fontSize="small" />;
      case RequestStatus.Approved:
        return <ApprovedIcon fontSize="small" />;
      default:
        return <PendingIcon fontSize="small" />;
    }
  };

  if (!request) {return null;}

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="request-details-dialog-title"
    >
      {/* Dialog Title */}
      <DialogTitle id="request-details-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {t('groupRequests.myRequests.detailDialog.title')}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label={t('common.close')}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent dividers>
        {/* Group Information Section */}
        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('groupRequests.myRequests.detailDialog.groupInformation')}
          </Typography>
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('groupRequests.details.groupName')}:
            </Typography>
            <Typography variant="body1" fontWeight={500} sx={{ mb: 2 }}>
              {request.group_name || t('groupRequests.approval.unknownGroup')}
            </Typography>

            {request.description && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t('groupRequests.details.description')}:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {request.description}
                </Typography>
              </>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('groupRequests.details.groupType')}:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {request.group_type
                ? t(`groupRequests.groupTypes.${request.group_type}`)
                : '-'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('groupRequests.details.visibility')}:
            </Typography>
            <Typography variant="body1">
              {request.visibility
                ? t(`groupRequests.visibility.${request.visibility}`)
                : '-'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Request Information Section */}
        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('groupRequests.myRequests.detailDialog.requestInformation')}
          </Typography>
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('groupRequests.details.requestedBy')}:
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {getUserDisplayName(request.user_id)}
            </Typography>
            {getUserEmail(request.user_id) && (
              <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {getUserEmail(request.user_id)}
              </Typography>
            )}
            {!getUserEmail(request.user_id) && <Box sx={{ mb: 2 }} />}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('groupRequests.details.justification')}:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {request.justification || request.reason || '-'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('groupRequests.details.requestedAt')}:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {request.requested_at
                ? formatDistanceToNow(new Date(request.requested_at), {
                    addSuffix: true,
                  })
                : '-'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('groupRequests.details.status')}:
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Chip
                icon={getStatusIcon(request.status || RequestStatus.Pending)}
                label={t(`groupRequests.status.${request.status || 'pending'}`)}
                color={getStatusColor(request.status || RequestStatus.Pending)}
                size="small"
              />
            </Box>

            {request.initial_admin_id && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t('groupRequests.details.initialAdmin')}:
                </Typography>
                <Typography variant="body1">
                  {getUserDisplayName(request.initial_admin_id)}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Selected Approvers Section */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('groupRequests.myRequests.detailDialog.selectedApprovers')} (
            {request.approver_ids?.length || 0})
          </Typography>
          {request.approver_ids && request.approver_ids.length > 0 ? (
            <List dense sx={{ pt: 0 }}>
              {request.approver_ids.map((approverId) => (
                <ListItem key={approverId} sx={{ px: 0 }}>
                  <ListItemText
                    primary={getUserDisplayName(approverId)}
                    primaryTypographyProps={{
                      variant: 'body2',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('groupRequests.approval.noApprovers')}
            </Typography>
          )}
        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
